import { useEffect, useState } from "react";
import { Avatar, Button, FormControl, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import clsx from "clsx";
import { TargetOrderStyles } from "../../styles/TargetOrder.style";
import { useAppDispatch } from "src/store/store";
import {
  setFilterRedux,
  setSortRedux,
  setTokenRedux,
  useTargetState,
} from "src/store/features/TargetOrders.slice";
import { trackEvent } from "src/Amplitude";
import { Box } from "@mui/system";
import { useGetTargetOrderTokensQuery } from "src/store/features/api/generated";

interface FiltersProps {
  disabled?: boolean;
}

const TargetOrdersFilters = (props: FiltersProps) => {
  const { disabled = false } = props;
  // redux
  const { data: targetTokens, isSuccess } = useGetTargetOrderTokensQuery();

  const sortOptions = [
    { id: 0, type: "Sort By", value: "none" },
    { id: 1, type: "Distance - Low", value: "targetClosest" },
    { id: 2, type: "Distance - High", value: "targetFarthest" },
    { id: 3, type: "Target Price - High", value: "targetHighest" },
    { id: 3, type: "Target Price - Low", value: "targetLowest" },
  ];

  const filterOptions = [
    { id: 0, type: "Filter By", value: "all" },
    { id: 1, type: "All", value: "all" },
    { id: 2, type: "Buy", value: "buy" },
    { id: 3, type: "Sell", value: "sell" },
  ];

  const [tokenOptions, setTokenOptions] = useState([
    { id: "0", name: "Select Token", value: "none", code: "none", logoUrl: "none" },
  ]);

  const appDispatch = useAppDispatch();
  const classes = TargetOrderStyles();
  const { filterOption, sortOption, filterToken } = useTargetState();

  // Update tokenOptions dynamically based on targetTokens
  useEffect(() => {
    if (targetTokens) {
      const filteredTokens = targetTokens.map((token) => ({
        id: token.code,
        code: token.code,
        name: token.name,
        value: token.code,
        type: token.type,
        logoUrl: token.logoUrl || "none",
      }));

      // Update the tokenOptions state
      setTokenOptions([
        { id: "0", name: "Select Token", value: "none", code: "none", logoUrl: "none" },
        ...filteredTokens,
      ]);
    }
    // Dependency array includes targetTokens
  }, [targetTokens]);

  const handleSortChange = (event: SelectChangeEvent) => {
    trackEvent("Target_Order_Sort_", {
      Target_Order_Sort: event.target.value,
    });
    appDispatch(setSortRedux(event.target.value));
  };

  const handleFilterChange = (event: SelectChangeEvent) => {
    trackEvent("Target_Order_Filter_", {
      Target_Order_Filter: event.target.value,
    });
    appDispatch(setFilterRedux(event.target.value));
  };

  const handleTokenChange = (event: SelectChangeEvent) => {
    const tokenValue = event.target.value;
    trackEvent("Target_Order_Token_Filter_", {
      Target_Order_Token_Filter: tokenValue,
    });
    appDispatch(setTokenRedux(tokenValue));
  };

  const clearFilters = () => {
    trackEvent("Target_Order_Clear_Filters");
    appDispatch(setFilterRedux("all"));
  };

  const clearSort = () => {
    trackEvent("Target_Order_Clear_Filters");
    appDispatch(setSortRedux("none"));
  };

  const clearToken = () => {
    trackEvent("Target_Order_Clear_Token");
    appDispatch(setTokenRedux("none"));
  };

  return (
    <>
      {/* Sort Dropdown */}
      <FormControl sx={{ m: 1, height: "22px" }}>
        <Select
          className={clsx(classes.filterSort, {
            [classes.sortSelected]: sortOption !== "none",
            [classes.filterDisabled]: disabled,
          })}
          variant="standard"
          labelId="sort-select"
          disableUnderline
          displayEmpty
          id="sort-select"
          autoWidth
          value={sortOption || ""}
          label="sort"
          onChange={handleSortChange}>
          {sortOptions.map((option, ind) => (
            <MenuItem className={classes.filterMenuItem} value={option.value} key={ind}>
              {option.type}
            </MenuItem>
          ))}
          <Box className={classes.clearBtnContainer}>
            <Button disableRipple onClick={clearSort} className={classes.clearBtn}>
              Clear
            </Button>
          </Box>
        </Select>
      </FormControl>

      {/* Filter Dropdown */}
      <FormControl sx={{ m: 1 }}>
        <Select
          labelId="filter-options"
          id="filter"
          className={clsx(classes.filter, {
            [classes.filterDisabled]: disabled,
            [classes.filterSelected]: filterOption !== "all",
          })}
          value={filterOption || ""}
          label="filter"
          displayEmpty
          disableUnderline
          variant="standard"
          autoWidth
          onChange={handleFilterChange}>
          {filterOptions.map((option, ind) => (
            <MenuItem className={classes.filterMenuItem} value={option.value} key={ind}>
              {option.type}
            </MenuItem>
          ))}
          <Box className={classes.clearBtnContainer}>
            <Button disableRipple onClick={clearFilters} className={classes.clearBtn}>
              Clear
            </Button>
          </Box>
        </Select>
      </FormControl>

      {/* Token Dropdown (Styled the same as Sort and Filter) */}
      {isSuccess && tokenOptions && tokenOptions.length > 1 && (
        <FormControl sx={{ m: 1 }}>
          <Select
            labelId="token-select"
            id="tokenSelect"
            className={classes.filterToken}
            sx={{
              ".Mui-disabled": {
                color: "#bdc2c5 !important", // Add !important to force it
                WebkitTextFillColor: "#bdc2c5 !important", // Add !important for WebKit browsers
              },
            }}
            value={filterToken || "none"}
            label="token"
            disabled={disabled || targetTokens.length <= 0}
            displayEmpty
            disableUnderline
            variant="standard"
            autoWidth
            onChange={handleTokenChange}>
            {tokenOptions.map((option, ind) => (
              <MenuItem className={classes.filterMenuItem} value={option.value} key={ind}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {option.logoUrl !== "none" && (
                    <Avatar
                      src={option.logoUrl}
                      alt={option.name}
                      sx={{ width: 14, height: 14, marginRight: 1 }} // Adjust size and margin
                    />
                  )}
                  {option.name}
                </Box>
              </MenuItem>
            ))}
            <Box className={classes.clearBtnContainer}>
              <Button disableRipple onClick={clearToken} id="clearToken" className={classes.clearBtn}>
                Clear Token
              </Button>
            </Box>
          </Select>
        </FormControl>
      )}
    </>
  );
};

export default TargetOrdersFilters;
